import * as React from "react";
import { JSX } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { toast } from "react-toastify";

import style from "./add-entitlement.scss";
import Button from "components/button/Button";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import { SuccessIcon } from "components/icons/SuccessIcon";
import layoutStyle from "components/layout/layout.scss";
import { SubpageLayout } from "components/layout/subpage-layout/SubpageLayout";
import { createProductIdToNameIbrMap } from "components/licenses/common";
import AddEntitlementForm, {
    FormValues,
    LicenseCount,
} from "components/licenses/entitlements/add-entitlement/AddEntitlementForm";
import Modal from "components/modal/Modal";
import { SUPPORT_EMAIL } from "domain/globalConstants";
import { Container, ContainerType, EmsConfiguration, LicenseData } from "domain/licenses";
import { licenseService, ProductRateList } from "services/licenses/LicenseService";
import { StoreState } from "store";
import buttonsStyle from "styles/buttons.scss";
import buttons from "styles/buttons.scss";

import testIds from "testIds.json";

interface Result {
    title: string;
    message: string;
}

interface Props {
    modalVisibility: boolean;
    emsConfiguration: EmsConfiguration;
    availableLicenses: LicenseData[];
    allContainers: Container[];
    onUpdate: () => void;
    setModalVisibility: (visibility: boolean) => void;
    mcUuid?: string;
    mcContainer?: string;
    tokenRates?: ProductRateList[];
    mcName?: string;
    preselectedProducts?: string[];
}

export interface EntitlementRequestLicense {
    blanccoProductId: number;
    licenseAmount: number;
}

const connector = connect((state: StoreState) => ({
    user: state.userReducer.user,
    theme: state.themeReducer.theme,
}));

const AddEntitlementWrapper = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const [result, setResult] = React.useState<Result>({ title: "", message: "" });
    const [resultVisible, setResultVisible] = React.useState(false);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const PRODUCT_ID_TO_NAME = createProductIdToNameIbrMap();
    const [submitDisabled, setSubmitDisabled] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState(false);
    const extractBlanccoProductId = (productId: string): string => {
        if (PRODUCT_ID_TO_NAME.get(productId)) {
            for (const [key, value] of PRODUCT_ID_TO_NAME) {
                if (key === productId) {
                    return value;
                }
            }
            return productId;
        } else {
            return productId;
        }
    };
    const addEntitlementSubmitEventHandler = async (
        { type, description, containerId }: FormValues,
        licensesCount: LicenseCount[]
    ): Promise<void> => {
        setLoading(true);
        const abortController = new AbortController();

        abortControllers.push(abortController);
        const { signal } = abortController;
        try {
            const licenses: EntitlementRequestLicense[] = [];
            licensesCount.forEach((license) => {
                licenses.push({
                    licenseAmount: license.assign,
                    blanccoProductId: parseInt(extractBlanccoProductId(license.productId)),
                });
            });
            await licenseService.createEntitlement(
                type,
                description,
                licenses,
                abortController,
                type === "SL_UPDATE" ? containerId : undefined,
                props.mcUuid
            );
        } catch {
            if (!signal.aborted) {
                showResult(
                    {
                        title: t("AddEntitlementsView.entitlementFailure"),
                        message: t("AddEntitlementsView.failureMessage", { email: SUPPORT_EMAIL }),
                    },
                    false
                );
            }
            return;
        }
        if (signal.aborted) {
            return;
        }
        showResult(
            {
                title: t("AddEntitlementsView.entitlementSuccess"),
                message: t("AddEntitlementsView.successMessage"),
            },
            true
        );
    };

    const showResult = (resultToShow: Result, successful: boolean) => {
        if (successful) {
            props.setModalVisibility(false);
            toast(
                <SuccessIcon
                    successClass={layoutStyle.customToastSuccessIcon}
                    color={props.theme.contentBackgroundColor}
                    text={resultToShow.message}
                />,
                {
                    closeButton: (closeToastProps) => (
                        <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={props.theme.iconFillColor} />
                    ),
                    className: layoutStyle.customToastSuccessMessage,
                }
            );
        } else {
            setResult(resultToShow);
            setResultVisible(true);
        }
    };

    const hideSuccessDialog = () => {
        props.onUpdate();
        setResultVisible(false);
        props.setModalVisibility(false);
    };

    React.useEffect(() => {
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, []);

    return (
        <>
            <SubpageLayout
                visible={props.modalVisibility}
                title={
                    props.mcName
                        ? t("AddEntitlementsView.titleForMc", { mcName: props.mcName })
                        : t("AddEntitlementsView.title")
                }
                loading={loading}
                buttons={
                    <>
                        <Button
                            type="submit"
                            className={buttons.primaryButtonWithoutIcon}
                            variant={"PRIMARY"}
                            form={"addEntitlementForm"}
                            disabled={submitDisabled}
                            testId={testIds.workArea.license.entitlements.createEntitlementDialog.createButton}
                        >
                            {t("AddEntitlementForm.createEntitlementButton")}
                        </Button>
                    </>
                }
            >
                <AddEntitlementForm
                    submitEventHandler={addEntitlementSubmitEventHandler}
                    fetchedTokenRates={props.tokenRates}
                    emsConfiguration={props.emsConfiguration}
                    availableLicenses={props.availableLicenses}
                    slContainers={
                        props.emsConfiguration.slEntitlements
                            ? props.allContainers.filter((container) =>
                                  [
                                      ContainerType.SL_ADMIN_MODE,
                                      ContainerType.SL_CREATE,
                                      ContainerType.SL_UPDATE,
                                  ].includes(container.type)
                              )
                            : []
                    }
                    mcUuid={props.mcUuid}
                    mcContainerId={props.mcContainer}
                    onDisableSubmit={setSubmitDisabled}
                    preselectedProducts={props.preselectedProducts}
                />
            </SubpageLayout>
            <Modal isOpen={resultVisible} hideModal={hideSuccessDialog} modalTitle={result.title}>
                <div className={style.resultContainer}>{result.message}</div>
                <div className={style.okButtonContainer}>
                    <button className={buttonsStyle.primaryButtonWithoutIcon} onClick={hideSuccessDialog}>
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default connector(AddEntitlementWrapper);
